import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Button } from "@/app/components/ui/button"
import { Checkbox } from "@/app/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/app/components/ui/radio-group"
import { Skeleton } from "@/app/components/ui/skeleton"
import { useCurrentUser } from "@/shared/contexts"
import { db } from "@/db/schema"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"
import {
  approveConsent,
  clearPendingConsentTx,
  denyConsent,
  describeConsentWithRetry,
  isConsentTxId,
  readPendingConsentTx,
  savePendingConsentTx,
  type ConsentAccess,
  type ConsentDescription,
} from "./oauthService"
import { summarizeConsent } from "./consentScopes"

/** How long leaving the page may wait for a first download that is still running. */
const SYNC_GRACE_MS = 8_000

/**
 * The page is shown as soon as the user is signed in, which can be while the first download of
 * their data is still running (see `DatabaseProvider deferInitialSync`). Navigating away aborts
 * that download and the next visit starts it over, so before leaving give it a short, bounded
 * moment — only when nothing has been downloaded yet (a finished download leaves rows behind).
 */
async function letInitialSyncFinish(): Promise<void> {
  const finished = (async () => {
    if ((await db.totodo.count()) > 0) return
    await waitForInitialSync(db, SYNC_GRACE_MS)
  })().catch(() => {})
  await Promise.race([finished, new Promise<void>((resolve) => setTimeout(resolve, SYNC_GRACE_MS))])
}

/**
 * The server answers with the app's own redirect URI (https, or http on a loopback
 * host). Anything else is refused here too — never navigate to `javascript:` or `data:`.
 */
function isSafeRedirect(value: string): boolean {
  try {
    const url = new URL(value)
    if (url.protocol === "https:") return true
    return (
      url.protocol === "http:" &&
      (url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]")
    )
  } catch {
    return false
  }
}

type PageState =
  | { phase: "loading" }
  | { phase: "ready"; description: ConsentDescription }
  | { phase: "expired" }
  | { phase: "error"; message?: string; detail?: string }
  | { phase: "redirecting" }

/** What failed, for the person to quote when reporting it: "HTTP 401 · Invalid token", "no answer". */
function failureDetail(status: number | undefined, code: string | undefined, noAnswer: string): string {
  const parts = [status === undefined ? noAnswer : `HTTP ${status}`]
  if (code) parts.push(code)
  return parts.join(" · ")
}

/**
 * OAuthConsentPage — `/oauth/consent?tx=<id>`.
 *
 * An MCP app (Claude, ChatGPT, …) sent the user to `api.totodo.io/authorize`; the API validated
 * the request, stored it server-side and redirected here with only a one-time transaction id.
 * The user sees who is asking, where the access will go and what it covers, chooses how much to
 * allow, and the API answers with the URL to send the browser to. The page never sees, builds
 * or edits a redirect URI itself, and never approves on its own.
 *
 * Rendered inside the login gate (DatabaseProvider + CurrentUserProvider) but outside AppLayout,
 * and as soon as the user is signed in — it needs the session token only, not the data download.
 */
export function OAuthConsentPage() {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const [searchParams] = useSearchParams()

  const tx = useMemo(() => {
    const fromUrl = searchParams.get("tx")
    if (isConsentTxId(fromUrl)) return fromUrl
    return readPendingConsentTx()
  }, [searchParams])

  const [state, setState] = useState<PageState>({ phase: "loading" })
  const [access, setAccess] = useState<ConsentAccess>("read")
  const [includeMemory, setIncludeMemory] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  /** Bumped by "Try again" on the error page: describe the same transaction once more. */
  const [describeRun, setDescribeRun] = useState(0)

  // "Preparing your views…" belongs to the app shell: the sidebar dismisses it, and this page has
  // no sidebar. Never let one raised on the way here stay on top of the dialog.
  useEffect(() => {
    toast.dismiss("preparing-views")
  }, [])

  useEffect(() => {
    if (!tx) {
      setState({ phase: "expired" })
      return
    }
    savePendingConsentTx(tx)
    let cancelled = false
    setState({ phase: "loading" })
    void describeConsentWithRetry(tx, () => cancelled).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setState({ phase: "ready", description: result.data })
      } else if (result.reason === "expired") {
        clearPendingConsentTx()
        setState({ phase: "expired" })
      } else {
        setState({ phase: "error", message: result.message, detail: failureDetail(result.status, result.code, t("no answer")) })
      }
    })
    return () => {
      cancelled = true
    }
    // `t` only formats the failure text; a language change must not restart the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx, describeRun])

  const leave = useCallback(async (redirectTo: string) => {
    clearPendingConsentTx()
    if (!isSafeRedirect(redirectTo)) {
      setState({ phase: "error" })
      return
    }
    setState({ phase: "redirecting" })
    await letInitialSyncFinish()
    window.location.assign(redirectTo)
  }, [])

  const handleApprove = useCallback(async () => {
    if (!tx) return
    setBusy(true)
    setNotice(null)
    try {
      const result = await approveConsent(tx, access, includeMemory)
      if (result.ok) return await leave(result.data.redirectTo)
      if (result.reason === "expired") {
        clearPendingConsentTx()
        setState({ phase: "expired" })
      } else {
        // A refused choice ("grants nothing") keeps the page so the user can change it.
        setNotice(result.message ?? t("Something went wrong. Please try again."))
      }
    } catch {
      setNotice(t("Something went wrong. Please try again."))
    } finally {
      setBusy(false)
    }
  }, [access, includeMemory, leave, t, tx])

  const handleDeny = useCallback(async () => {
    if (!tx) return
    setBusy(true)
    setNotice(null)
    try {
      const result = await denyConsent(tx)
      if (result.ok) return await leave(result.data.redirectTo)
      clearPendingConsentTx()
      setState({ phase: result.reason === "expired" ? "expired" : "error" })
    } catch {
      setNotice(t("Something went wrong. Please try again."))
    } finally {
      setBusy(false)
    }
  }, [leave, t, tx])

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md space-y-5 rounded-2xl border border-foreground/9 p-6 shadow-sm">
        {state.phase === "loading" || state.phase === "redirecting" ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-24 w-full" />
            {state.phase === "redirecting" && (
              <p className="text-sm text-muted-foreground">{t("Sending you back to the app…")}</p>
            )}
          </div>
        ) : state.phase === "expired" ? (
          <div className="space-y-3">
            <h1 className="text-lg font-semibold">{t("This request has expired")}</h1>
            <p className="text-sm text-muted-foreground">
              {t(
                "This sign-in request has expired or was already used. Go back to the app and connect again.",
              )}
            </p>
            <a href="/" className="text-sm text-primary hover:underline">
              {t("Open To To-Do")}
            </a>
          </div>
        ) : state.phase === "error" ? (
          <div className="space-y-3">
            <h1 className="text-lg font-semibold">{t("Something went wrong")}</h1>
            <p className="text-sm text-muted-foreground">
              {state.message ?? t("We could not complete this request. Go back to the app and try again.")}
            </p>
            {state.detail && <p className="text-xs text-muted-foreground">{state.detail}</p>}
            <div className="flex items-center gap-4">
              {/* Only a failed describe: after approve has spent the transaction, trying again cannot help. */}
              {tx && state.detail && (
                <Button variant="primary" onClick={() => setDescribeRun((run) => run + 1)}>
                  {t("Try again")}
                </Button>
              )}
              <a href="/" className="text-sm text-primary hover:underline">
                {t("Open To To-Do")}
              </a>
            </div>
          </div>
        ) : (
          <ConsentForm
            description={state.description}
            email={user.email}
            access={access}
            onAccessChange={setAccess}
            includeMemory={includeMemory}
            onIncludeMemoryChange={setIncludeMemory}
            busy={busy}
            notice={notice}
            onApprove={() => void handleApprove()}
            onDeny={() => void handleDeny()}
          />
        )}
      </div>
    </div>
  )
}

interface ConsentFormProps {
  description: ConsentDescription
  email: string
  access: ConsentAccess
  onAccessChange: (access: ConsentAccess) => void
  includeMemory: boolean
  onIncludeMemoryChange: (value: boolean) => void
  busy: boolean
  notice: string | null
  onApprove: () => void
  onDeny: () => void
}

function ConsentForm({
  description,
  email,
  access,
  onAccessChange,
  includeMemory,
  onIncludeMemoryChange,
  busy,
  notice,
  onApprove,
  onDeny,
}: ConsentFormProps) {
  const { t } = useTranslation()
  const { client, redirect, scopes } = description

  // What approving grants with the current choices (requested ∩ chosen — the server applies the
  // same rule), and what the app asked for that those choices leave out.
  const summary = useMemo(
    () => summarizeConsent(scopes, access, includeMemory),
    [scopes, access, includeMemory],
  )
  const withheld = [
    ...summary.withheldResources.map((label) => t(label)),
    ...(summary.withheldChanges ? [t("changes to your data")] : []),
  ]

  return (
    <>
      <div className="space-y-1.5">
        <h1 className="text-lg font-semibold">
          {t("Allow {{name}} to use your To To-Do?", { name: client.name })}
        </h1>
        {email && (
          <p className="text-xs text-muted-foreground">
            {t("Signed in as {{email}}", { email })}
          </p>
        )}
      </div>

      <div className="space-y-2 text-sm">
        {client.nameVerified && client.domain ? (
          <p>
            {t("Published by")} <strong>{client.domain}</strong>
          </p>
        ) : (
          <p className="text-muted-foreground">
            {t("Unverified app: this app chose its own name, and we have not checked it.")}
          </p>
        )}
        <p className="text-muted-foreground">
          {t("After you approve, you will be sent to")} <strong className="text-foreground">{redirect.host}</strong>
        </p>
        {redirect.isLoopback && (
          <div
            role="alert"
            className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-foreground"
          >
            {t(
              "This sends access to an app on your own computer. Continue only if you just started signing in from it.",
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium">{t("This gives it access to")}</h2>
        {summary.granted.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {summary.granted.map(({ label, canWrite }) => (
              <li
                key={label}
                className="rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground"
              >
                {t(label)}
                {canWrite ? "" : ` (${t("read only")})`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("With these choices the app would get no access.")}
          </p>
        )}
        {withheld.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t("Asked for but not included")}: {withheld.join(", ")}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium">{t("How much do you allow?")}</h2>
        <RadioGroup
          value={access}
          onValueChange={(value) => onAccessChange(value as ConsentAccess)}
          className="gap-3"
          aria-label={t("How much do you allow?")}
        >
          <label className="flex cursor-pointer items-start gap-3">
            <RadioGroupItem value="read" disabled={busy} />
            <span className="text-sm">
              <span className="font-medium">{t("Read")}</span>
              <span className="block text-muted-foreground">
                {t("The app can look at your data but cannot change it.")}
              </span>
            </span>
          </label>
          {description.canWrite && (
            <label className="flex cursor-pointer items-start gap-3">
              <RadioGroupItem value="read-write" disabled={busy} />
              <span className="text-sm">
                <span className="font-medium">{t("Read and change")}</span>
                <span className="block text-muted-foreground">
                  {t("The app can also create, edit, complete and delete.")}
                </span>
              </span>
            </label>
          )}
        </RadioGroup>

        {description.hasMemory && (
          <label className="flex cursor-pointer items-start gap-3 pt-1">
            <Checkbox
              checked={includeMemory}
              onCheckedChange={onIncludeMemoryChange}
              disabled={busy}
              aria-label={t("Include AI memory")}
            />
            <span className="text-sm">
              <span className="font-medium">{t("Include AI memory")}</span>
              <span className="block text-muted-foreground">
                {t("What your AI assistant has learned about you. Off unless you turn it on.")}
              </span>
            </span>
          </label>
        )}
      </div>

      {notice && (
        <p role="alert" className="text-sm text-red-600">
          {notice}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onDeny} disabled={busy}>
          {t("Deny")}
        </Button>
        <Button
          variant="primary"
          onClick={onApprove}
          disabled={busy || summary.granted.length === 0}
        >
          {t("Approve")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {t("You can disconnect apps at any time in Settings, under API.")}
      </p>
    </>
  )
}
